import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatPrice, categoryLabels } from '../utils/api';
import ProductCard from '../components/shop/ProductCard';
import './Home.css';

const HERO_CATEGORIES = [
  { key: 'indoor-plants', emoji: '🪴', label: 'Indoor Plants' },
  { key: 'succulents', emoji: '🌵', label: 'Succulents' },
  { key: 'herbs', emoji: '🌿', label: 'Herbs' },
  { key: 'tools', emoji: '🔧', label: 'Garden Tools' },
  { key: 'pots-planters', emoji: '🏺', label: 'Pots & Planters' },
  { key: 'soil-fertilizers', emoji: '🪱', label: 'Soil & Fertilizers' },
];

const WHY_US = [
  { emoji: '🚚', title: 'Free Shipping', desc: 'On orders above ₹999' },
  { emoji: '🌱', title: 'Healthy Plants', desc: 'Expert-packed for safe delivery' },
  { emoji: '↩️', title: '7-Day Returns', desc: 'Unhappy? We\'ll make it right' },
  { emoji: '💬', title: '24/7 Support', desc: 'Plant experts at your service' },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/products/featured')
      .then(r => setFeatured(r.data.products))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content container">
          <div className="hero-text">
            <span className="hero-eyebrow">🌿 India's Premium Plant Store</span>
            <h1 className="hero-title">
              Bring Nature<br />
              <span className="hero-accent">Indoors</span>
            </h1>
            <p className="hero-desc">
              Discover hundreds of hand-picked plants, premium pots, and professional gardening equipment delivered right to your door.
            </p>
            <div className="hero-cta">
              <Link to="/shop" className="btn btn-primary btn-lg">Shop Now</Link>
              <Link to="/shop?featured=true" className="btn btn-secondary btn-lg">Featured Picks</Link>
            </div>
            <div className="hero-stats">
              <div className="stat"><strong>500+</strong><span>Plant varieties</span></div>
              <div className="stat-divider"></div>
              <div className="stat"><strong>10k+</strong><span>Happy customers</span></div>
              <div className="stat-divider"></div>
              <div className="stat"><strong>4.8★</strong><span>Average rating</span></div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-circle"></div>
            <img
              src="https://images.unsplash.com/photo-1603912699214-92627f304eb6?w=600"
              alt="Beautiful monstera plant"
              className="hero-image"
            />
            <div className="hero-card hero-card-1">
              <span>🌡️</span>
              <div><strong>18–24°C</strong><br /><small>Ideal indoor temp</small></div>
            </div>
            <div className="hero-card hero-card-2">
              <span>💧</span>
              <div><strong>Weekly</strong><br /><small>Easy watering</small></div>
            </div>
          </div>
        </div>
      </section>

      {/* Category pills */}
      <section className="categories-section container">
        <div className="section-header">
          <h2 className="section-title">Shop by Category</h2>
          <Link to="/shop" className="see-all">See all →</Link>
        </div>
        <div className="category-grid">
          {HERO_CATEGORIES.map(cat => (
            <button
              key={cat.key}
              className="category-pill"
              onClick={() => navigate(`/shop?category=${cat.key}`)}
            >
              <span className="cat-emoji">{cat.emoji}</span>
              <span className="cat-label">{cat.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="featured-section container">
        <div className="section-header">
          <h2 className="section-title">Featured Plants & Equipment</h2>
          <Link to="/shop?featured=true" className="see-all">See all →</Link>
        </div>
        {loading ? (
          <div className="loading-center"><div className="spinner"></div></div>
        ) : (
          <div className="product-grid">
            {featured.slice(0, 8).map(p => <ProductCard key={p._id} product={p} />)}
          </div>
        )}
      </section>

      {/* Why us banner */}
      <section className="why-section">
        <div className="container">
          <div className="why-grid">
            {WHY_US.map(w => (
              <div key={w.title} className="why-item">
                <span className="why-emoji">{w.emoji}</span>
                <div>
                  <strong>{w.title}</strong>
                  <p>{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="cta-banner container">
        <div className="cta-inner">
          <h2>Ready to Start Your Green Journey?</h2>
          <p>Join 10,000+ plant parents who trust PlantShop for quality plants and expert advice.</p>
          <Link to="/register" className="btn btn-primary btn-lg">Get Started — It's Free</Link>
        </div>
      </section>
    </div>
  );
}
